/* eslint-disable max-lines */
import { BookabilityStatus, type OpenShift } from "@clipboard-health/contract-worker-app-bff";
import { isDefined } from "@clipboard-health/util-ts";
import { addHours, differenceInHours, parseISO, subHours } from "date-fns";

import { APP_V2_APP_EVENTS, logError } from "../../../lib/analytics";
import { formatCentsAsUsd } from "../../../lib/Money/utils/currency";
import { OpenShiftWorkplace } from "../Open/types";
import { doShiftsExcessivelyOverlap } from "./doShiftsExcessivelyOverlap";
import type {
  BookedShiftWithContext,
  ShiftBundle,
  ShiftBundlesConfig,
  ShiftTimeData,
} from "./types";
import {
  OVERWORKING_WINDOW_SIZE_IN_HOURS,
  wouldProposedShiftCauseOverworking,
} from "./wouldProposedShiftCauseOverworking";

export function getBundleLabel(requiredShiftCount: number, totalShiftCount: number): string {
  if (requiredShiftCount <= 1) {
    return "Shift Bundle";
  }

  return requiredShiftCount === totalShiftCount
    ? `${totalShiftCount} shift bundle`
    : `${requiredShiftCount}+ shift bundle`;
}

function createBundleId(workplaceId: string, qualification: string): string {
  return `bundle-${workplaceId}-${qualification}`;
}

export function isSingleOrientationShift(shift: OpenShift): boolean {
  return shift.attributes.orientationShiftType === "single-shift";
}

export function calculateRequiredShiftCount({
  availableShiftCount,
  config,
  bookedShifts,
  workplaceId,
  recentShiftCount = 0,
}: {
  availableShiftCount: number;
  config: Extract<ShiftBundlesConfig, { enabled: true }>;
  bookedShifts: BookedShiftWithContext[];
  workplaceId: string;
  recentShiftCount?: number;
}): number {
  const { percentageOfAvailableShiftsRequiredToBundle, maxRequiredShifts, minimumBundleSize } =
    config;
  const raw = Math.ceil(availableShiftCount * percentageOfAvailableShiftsRequiredToBundle);
  const baseRequiredShiftCount = Math.min(Math.max(raw, minimumBundleSize), maxRequiredShifts);

  const alreadyBookedCount = bookedShifts.filter(
    (shift) => shift.workplaceId === workplaceId
  ).length;

  const remainingRequiredShiftCount =
    baseRequiredShiftCount - alreadyBookedCount - recentShiftCount;
  return Math.max(1, remainingRequiredShiftCount);
}

type ShiftBundleEligibilityParams =
  | {
      isOrientationBundle: true;
      bookedShifts: ShiftTimeData[];
    }
  | {
      isOrientationBundle: false;
      bookedShifts: ShiftTimeData[];
      leadTimeInHours: number;
    };

/**
 * Checks if a shift is eligible for bundling based on:
 * 1. Starts after the lead time threshold (for non-orientation shifts)
 * 2. Is bookable (not windowed out)
 * 3. Would not cause overworking when combined with booked shifts
 */
function isShiftEligibleForBundle(shift: OpenShift, params: ShiftBundleEligibilityParams) {
  const { bookedShifts, isOrientationBundle } = params;
  const now = new Date();

  const shiftStart = parseISO(shift.attributes.start);
  if (!isOrientationBundle && differenceInHours(shiftStart, now) < params.leadTimeInHours) {
    return false;
  }

  if (shift.attributes.bookability?.status !== BookabilityStatus.BOOKABLE) {
    return false;
  }

  const shiftData = {
    start: shift.attributes.start,
    end: shift.attributes.end,
  };
  if (wouldProposedShiftCauseOverworking(shiftData, bookedShifts)) {
    return false;
  }

  return true;
}

/**
 * Finds compatible companion shifts for a given shift.
 * Companions must not cause overworking or excessively overlap with the shift.
 */
export function findCompatibleCompanionShifts(params: {
  anchorShift: OpenShift;
  candidateShifts: OpenShift[];
  bookedShifts: ShiftTimeData[];
  isOrientationBundle?: boolean;
}): OpenShift[] {
  const { anchorShift, candidateShifts, bookedShifts, isOrientationBundle = false } = params;
  const anchorData = {
    start: anchorShift.attributes.start,
    end: anchorShift.attributes.end,
  };

  const isAnchorOrientationShift = isSingleOrientationShift(anchorShift);

  if (isOrientationBundle && !isAnchorOrientationShift) {
    // TODO (TFR-236) - Bundle regular shift anchor into orientation bundle
    return [];
  }

  return candidateShifts.filter((companionShift) => {
    if (companionShift.id === anchorShift.id) {
      return true; // Always include the anchor shift itself
    }

    const companionData = {
      start: companionShift.attributes.start,
      end: companionShift.attributes.end,
    };

    const anchorStart = parseISO(anchorData.start);
    const anchorEnd = parseISO(anchorData.end);
    const companionStart = parseISO(companionData.start);
    const companionEnd = parseISO(companionData.end);
    const windowEnd = addHours(anchorStart, OVERWORKING_WINDOW_SIZE_IN_HOURS);
    const windowStart = subHours(anchorEnd, OVERWORKING_WINDOW_SIZE_IN_HOURS);

    const isCompanionBeforeAnchor = companionEnd < anchorStart;
    if (isOrientationBundle && isAnchorOrientationShift && isCompanionBeforeAnchor) {
      // Companion shifts in an orientation bundle must be after orientation anchor
      return false;
    }

    // If companion starts after anchor's forward 24-hour window ends, it's compatible
    if (companionStart >= windowEnd) {
      return true;
    }

    // If companion ends before anchor's backward 24-hour window starts, it's compatible
    if (companionEnd <= windowStart) {
      return true;
    }

    // Exclude companions that overlap with the anchor shift by more than 30 minutes
    if (doShiftsExcessivelyOverlap(anchorData, companionData)) {
      return false;
    }

    // Otherwise, check if adding companion to (anchor + booked shifts) would cause overworking
    return !wouldProposedShiftCauseOverworking(companionData, [anchorData, ...bookedShifts]);
  });
}

/**
 * Gets the bundle for a single specific shift.
 *
 * @param targetShiftId - The ID of the shift to get the bundle for
 * @param workplaceShifts - All shifts at the same workplace with the same qualification
 * @param config - Bundle configuration from feature flag
 * @param bookedShifts - Worker's currently booked shifts
 * @param isOrientationBundle - Whether to bundle with an orientation shift
 * @returns The bundle for the target shift, or undefined if not eligible
 */
export function getBundleForShift({
  targetShiftId,
  workplaceShifts,
  config,
  bookedShifts,
  recentShiftCount = 0,
  isOrientationBundle = false,
}: {
  targetShiftId: string;
  workplaceShifts: OpenShift[];
  config: ShiftBundlesConfig;
  bookedShifts: BookedShiftWithContext[];
  recentShiftCount?: number;
  isOrientationBundle?: boolean;
}): ShiftBundle | undefined {
  if (!config.enabled) {
    return undefined;
  }

  const { leadTimeInHours, minimumBundleSize } = config;

  const targetShift = workplaceShifts.find((shift) => shift.id === targetShiftId);
  if (!targetShift) {
    return undefined;
  }

  if (isOrientationBundle && !isSingleOrientationShift(targetShift)) {
    // TODO (TFR-236): Create orientation bundle from regular target (anchor) shifts
    return undefined;
  }

  if (!isOrientationBundle && isSingleOrientationShift(targetShift)) {
    // An orientation shift should only be bundled as an orientation bundle
    return undefined;
  }

  const shiftEligibilityParams: ShiftBundleEligibilityParams = isOrientationBundle
    ? { isOrientationBundle: true, bookedShifts }
    : { isOrientationBundle: false, bookedShifts, leadTimeInHours };
  if (!isShiftEligibleForBundle(targetShift, shiftEligibilityParams)) {
    return undefined;
  }

  const workplaceId = targetShift.relationships.workplace.data.id;
  const { qualification } = targetShift.attributes;

  const eligibleShifts = workplaceShifts.filter((shift) => {
    if (shift.attributes.qualification !== qualification) {
      return false;
    }

    return isShiftEligibleForBundle(shift, shiftEligibilityParams);
  });

  if (eligibleShifts.length < minimumBundleSize) {
    return undefined;
  }

  // Orientation bundles may contain at most one orientation shift. Other orientation
  // shifts are filtered out of the candidate pool
  const candidateShifts = isOrientationBundle
    ? [targetShift, ...eligibleShifts.filter((shift) => !isSingleOrientationShift(shift))]
    : eligibleShifts;

  const compatibleShifts = findCompatibleCompanionShifts({
    anchorShift: targetShift,
    candidateShifts,
    bookedShifts,
    isOrientationBundle,
  });

  if (compatibleShifts.length < minimumBundleSize) {
    return undefined;
  }

  const requiredShiftCount = calculateRequiredShiftCount({
    availableShiftCount: compatibleShifts.length,
    config,
    bookedShifts,
    workplaceId,
    recentShiftCount,
  });

  return {
    workplaceId,
    qualification,
    shifts: compatibleShifts,
    totalShiftCount: compatibleShifts.length,
    requiredShiftCount,
    isOrientationBundle,
  };
}

/**
 * Groups shifts into bundles by (workplaceId, qualification), filtering out shifts that
 * would cause overworking (>17 hours within any 24-hour window) or excessively overlap
 * (>30 minutes of overlap).
 *
 * A shift is eligible for bundling if:
 * 1. Its workplace is in the `eligibleWorkplaceIds` set
 * 2. It starts more than `leadTimeInHours` from now
 * 3. It is not windowed out
 * 4. It doesn't cause overworking when combined with booked shifts
 *
 * Each shift gets its own bundle with filtered companion shifts (excluding those that
 * would cause overworking if booked together or that excessively overlap with the anchor
 * shift). A shift only gets a bundle if it has at least `minimumBundleSize` compatible
 * companions.
 *
 * Returns a map of shiftId → ShiftBundle.
 */
export function groupShiftsIntoBundles({
  shifts,
  config,
  eligibleWorkplaceIds,
  recentWorkplaceWorkedShiftCounts = new Map<string, number>(),
  bookedShifts,
}: {
  shifts: OpenShift[];
  config: ShiftBundlesConfig;
  eligibleWorkplaceIds: Set<string>;
  recentWorkplaceWorkedShiftCounts?: Map<string, number>;
  bookedShifts: BookedShiftWithContext[];
}): Map<string, ShiftBundle> {
  if (!config.enabled) {
    return new Map();
  }

  const { leadTimeInHours, minimumBundleSize } = config;
  const eligibilityParams = { leadTimeInHours, bookedShifts, isOrientationBundle: false };

  const groups = groupShiftsByWorkplaceAndQualification({
    shifts,
    minimumBundleSize,
    shiftEligibilityParams: eligibilityParams,
    eligibleWorkplaceIds,
  });

  for (const [bundleId, group] of groups) {
    if (group.shifts.length < minimumBundleSize) {
      groups.delete(bundleId);
    }
  }

  const bundlesByShiftId = new Map<string, ShiftBundle>();

  for (const group of groups.values()) {
    for (const shift of group.shifts) {
      const compatibleShifts = findCompatibleCompanionShifts({
        anchorShift: shift,
        candidateShifts: group.shifts,
        bookedShifts,
      });

      // Only create bundle if it meets minimum size requirement
      if (compatibleShifts.length < minimumBundleSize) {
        // Skip this shift - it doesn't have enough compatible companions
        // But continue processing other shifts in the group
        continue;
      }

      const requiredShiftCount = calculateRequiredShiftCount({
        availableShiftCount: compatibleShifts.length,
        config,
        bookedShifts,
        workplaceId: group.workplaceId,
        recentShiftCount: recentWorkplaceWorkedShiftCounts.get(group.workplaceId) ?? 0,
      });

      const bundle: ShiftBundle = {
        workplaceId: group.workplaceId,
        qualification: group.qualification,
        shifts: compatibleShifts,
        totalShiftCount: compatibleShifts.length,
        requiredShiftCount,
        isOrientationBundle: false,
      };

      bundlesByShiftId.set(shift.id, bundle);
    }
  }

  return bundlesByShiftId;
}

export function estimateBundleTotalPayInMinorUnits(
  anchorShiftPayInMinorUnits: number,
  totalShiftCount: number
): number {
  const raw = anchorShiftPayInMinorUnits * totalShiftCount;
  const roundingUnit = raw < 100_000 ? 1_000 : 10_000;
  return Math.floor(raw / roundingUnit) * roundingUnit;
}

export function formatApproximatePayInCentsAsUsd(totalPayInMinorUnits: number): string {
  return `~${formatCentsAsUsd(totalPayInMinorUnits, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;
}

export function hasWorkerOrientedAtWorkplace({
  workplaceId,
  workplacesWithOrientationCredentials,
  bookedShifts,
}: {
  workplaceId: string;
  workplacesWithOrientationCredentials: Set<string>;
  bookedShifts: BookedShiftWithContext[];
}): boolean {
  if (workplacesWithOrientationCredentials.has(workplaceId)) {
    return true;
  }

  // Worker with upcoming shift booked is considered oriented
  return isDefined(bookedShifts.find((shift) => shift.workplaceId === workplaceId));
}

export function isWorkplaceOrientationBundleEligible({
  orientationShiftKind,
  workplaceId,
  workplacesWithOrientationCredentials,
  bookedShifts,
}: {
  orientationShiftKind: string | undefined;
  workplaceId: string;
  workplacesWithOrientationCredentials: Set<string>;
  bookedShifts: BookedShiftWithContext[];
}): boolean {
  return (
    orientationShiftKind === "single-shift" &&
    !hasWorkerOrientedAtWorkplace({
      workplaceId,
      workplacesWithOrientationCredentials,
      bookedShifts,
    })
  );
}

export function findAndPartitionEligibleWorkplaces({
  workplacesMap,
  workplacesWithOrientationCredentials,
  bookedShifts,
  skipOrientationWorkplaces = false,
}: {
  workplacesMap: Map<string, OpenShiftWorkplace>;
  workplacesWithOrientationCredentials: Set<string>;
  bookedShifts: BookedShiftWithContext[];
  skipOrientationWorkplaces?: boolean;
}) {
  const regularBundleWorkplaceIds = new Set<string>();
  const orientationBundleWorkplaceIds = new Set<string>();

  for (const [workplaceId, workplace] of workplacesMap) {
    const isOrientationBundleEligible = isWorkplaceOrientationBundleEligible({
      orientationShiftKind: workplace.attributes.orientationShiftKind,
      workplaceId,
      workplacesWithOrientationCredentials,
      bookedShifts,
    });

    if (isOrientationBundleEligible && !skipOrientationWorkplaces) {
      orientationBundleWorkplaceIds.add(workplaceId);
    } else if (workplace.attributes.isShiftBundlesEnabled) {
      regularBundleWorkplaceIds.add(workplaceId);
    }
  }

  return {
    regularBundleWorkplaceIds,
    orientationBundleWorkplaceIds,
  };
}

export function groupShiftsByWorkplaceAndQualification({
  shifts,
  minimumBundleSize,
  shiftEligibilityParams,
  eligibleWorkplaceIds,
}: {
  shifts: OpenShift[];
  minimumBundleSize: number;
  shiftEligibilityParams: ShiftBundleEligibilityParams;
  eligibleWorkplaceIds: Set<string>;
}): Map<string, { workplaceId: string; qualification: string; shifts: OpenShift[] }> {
  const groups = new Map<
    string,
    { workplaceId: string; qualification: string; shifts: OpenShift[] }
  >();

  for (const shift of shifts) {
    const workplaceId = shift.relationships.workplace.data.id;

    if (!eligibleWorkplaceIds.has(workplaceId)) {
      continue;
    }

    if (!isShiftEligibleForBundle(shift, shiftEligibilityParams)) {
      continue;
    }

    const { qualification } = shift.attributes;
    const key = createBundleId(workplaceId, qualification);

    const existing = groups.get(key);
    if (existing) {
      existing.shifts.push(shift);
    } else {
      groups.set(key, { workplaceId, qualification, shifts: [shift] });
    }
  }

  for (const [bundleId, group] of groups) {
    if (group.shifts.length < minimumBundleSize) {
      groups.delete(bundleId);
    }
  }

  return groups;
}

export function groupShiftsIntoOrientationBundles({
  shifts,
  config,
  eligibleWorkplaceIds,
  recentWorkplaceWorkedShiftCounts = new Map<string, number>(),
  bookedShifts,
}: {
  shifts: OpenShift[];
  config: ShiftBundlesConfig;
  eligibleWorkplaceIds: Set<string>;
  recentWorkplaceWorkedShiftCounts?: Map<string, number>;
  bookedShifts: BookedShiftWithContext[];
}): Map<string, ShiftBundle> {
  if (!config.enabled) {
    return new Map();
  }

  const { minimumBundleSize } = config;
  const eligibilityParams: ShiftBundleEligibilityParams = {
    bookedShifts,
    isOrientationBundle: true,
  };

  const groups = groupShiftsByWorkplaceAndQualification({
    shifts,
    minimumBundleSize,
    shiftEligibilityParams: eligibilityParams,
    eligibleWorkplaceIds,
  });

  const bundlesByShiftId = new Map<string, ShiftBundle>();

  for (const group of groups.values()) {
    // Orientation bundles can have at most one orientation shift, so we include only regular
    // shifts as candidates for bundling.
    const regularShifts = group.shifts.filter((shift) => !isSingleOrientationShift(shift));

    for (const shift of group.shifts) {
      const candidateShifts = isSingleOrientationShift(shift)
        ? [shift, ...regularShifts]
        : regularShifts;
      const compatibleShifts = findCompatibleCompanionShifts({
        anchorShift: shift,
        candidateShifts,
        bookedShifts,
        isOrientationBundle: true,
      });

      // Only create bundle if it meets minimum size requirement
      if (compatibleShifts.length < minimumBundleSize) {
        // Skip this shift - it doesn't have enough compatible companions
        // But continue processing other shifts in the group
        continue;
      }

      const requiredShiftCount = calculateRequiredShiftCount({
        availableShiftCount: compatibleShifts.length,
        config,
        bookedShifts,
        workplaceId: group.workplaceId,
        recentShiftCount: recentWorkplaceWorkedShiftCounts.get(group.workplaceId) ?? 0,
      });

      const bundle: ShiftBundle = {
        workplaceId: group.workplaceId,
        qualification: group.qualification,
        shifts: compatibleShifts,
        totalShiftCount: compatibleShifts.length,
        requiredShiftCount,
        isOrientationBundle: true,
      };

      bundlesByShiftId.set(shift.id, bundle);
    }
  }

  return bundlesByShiftId;
}

export function mergeBundles(bundles: Array<Map<string, ShiftBundle>>) {
  const merged = new Map<string, ShiftBundle>();
  for (const bundle of bundles) {
    for (const [shiftId, shiftBundle] of bundle) {
      merged.set(shiftId, shiftBundle);
    }
  }

  return merged;
}

export function sortBundleShifts(bundle: ShiftBundle | undefined): OpenShift[] {
  if (!isDefined(bundle)) {
    return [];
  }

  const bundleShifts = bundle.shifts;

  const sortedRegularShifts = bundleShifts
    .filter((shift) => !isSingleOrientationShift(shift))
    .sort(
      (a, b) => parseISO(a.attributes.start).getTime() - parseISO(b.attributes.start).getTime()
    );
  const orientationShift = bundleShifts.find((shift) => isSingleOrientationShift(shift));

  if (bundle.isOrientationBundle && !isDefined(orientationShift)) {
    logError(APP_V2_APP_EVENTS.SHIFT_BUNDLE_MISSING_ORIENTATION_SHIFT_FAILURE, {
      error: new Error("Orientation bundle missing orientation shift"),
      metadata: {
        workplaceId: bundle.workplaceId,
        qualification: bundle.qualification,
        totalShiftCount: bundle.totalShiftCount,
      },
    });
  }

  return isDefined(orientationShift)
    ? [orientationShift, ...sortedRegularShifts]
    : sortedRegularShifts;
}

/* eslint-enable max-lines */
